import React from "react";
import { H1, H2, H3, P, Ol, Line, DoubleLine, HangingP, Indent, Subheader } from "../DsmComponents";

export default function AdhdPage() {
  return (
    <div style={{ 
      fontFamily: 'system-ui, Arial, sans-serif',
      lineHeight: 1.6,
      maxWidth: '800px',
      margin: '2rem auto',
    }}>
      <H1>Attention-Deficit/Hyperactivity Disorder</H1>
      <Line />
      <H2>Attention-Deficit/Hyperactivity Disorder</H2>
      <DoubleLine />

      <H3>Diagnostic Criteria</H3>
      <Line />

      <HangingP>
        <strong>A.</strong> A persistent pattern of inattention and/or
        hyperactivity-impulsivity that interferes with functioning or
        development, as characterized by (1) and/or (2):
      </HangingP>

      {/* 1. Inattention */}
      <HangingP>
        <strong>1. Inattention:</strong> Six (or more) of the following symptoms
        have persisted for at least 6 months to a degree that is inconsistent
        with developmental level and that negatively impacts directly on social
        and academic/occupational activities:
      </HangingP>

      <P>
        <strong>Note:</strong> The symptoms are not solely a manifestation of
        oppositional behavior, defiance, hostility, or failure to understand
        tasks or instructions. For older adolescents and adults (age 17 and
        older), at least five symptoms are required.
      </P>

      <Ol>
        <li>
          Often fails to give close attention to details or makes careless
          mistakes…
        </li>
        <li>Often has difficulty sustaining attention…</li>
        <li>Often does not seem to listen when spoken to directly…</li>
        <li>Often does not follow through on instructions…</li>
        <li>Often has difficulty organizing tasks and activities…</li>
        <li>
          Often avoids, dislikes, or is reluctant to engage in tasks that
          require sustained mental effort…
        </li>
        <li>Often loses things necessary for tasks or activities…</li>
        <li>Is often easily distracted by extraneous stimuli…</li>
        <li>Is often forgetful in daily activities…</li>
      </Ol>

      {/* 2. Hyperactivity & Impulsivity */}
      <HangingP>
        <strong>2. Hyperactivity and Impulsivity:</strong> Six (or more) of the
        following symptoms have persisted for at least 6 months to a degree that
        is inconsistent with developmental level and that negatively impacts
        directly on social and academic/occupational activities:
      </HangingP>

      <P>
        <strong>Note:</strong> The symptoms are not solely a manifestation of
        oppositional behavior, defiance, hostility, or a failure to understand
        tasks or instructions. For older adolescents and adults (age 17 and
        older), at least five symptoms are required.
      </P>

      <Ol>
        <li>Often fidgets with or taps hands or feet or squirms in seat.</li>
        <li>
          Often leaves seat in situations when remaining seated is expected…
        </li>
        <li>
          Often runs about or climbs in situations where it is inappropriate.{" "}
          <em>Note:</em> In adolescents or adults, may be limited to feeling
          restless.
        </li>
        <li>Often unable to play or engage in leisure activities quietly.</li>
        <li>
          Is often “on the go,” acting as if “driven by a motor” (e.g., unable
          to be still for extended time)…
        </li>
        <li>Often talks excessively.</li>
        <li>
          Often blurts out an answer before a question has been completed…
        </li>
        <li>Often has difficulty waiting their turn…</li>
        <li>Often interrupts or intrudes on others…</li>
      </Ol>

      {/* B–E */}
      <HangingP>
        <strong>B.</strong> Several inattentive or hyperactive-impulsive
        symptoms were present prior to age 12 years.
      </HangingP>
      <HangingP>
        <strong>C.</strong> Several inattentive or hyperactive-impulsive
        symptoms are present in two or more settings (e.g., home, school, work,
        with friends or relatives, or in other activities).
      </HangingP>
      <HangingP>
        <strong>D.</strong> There is clear evidence that the symptoms interfere
        with, or reduce the quality of, social, academic, or occupational
        functioning.
      </HangingP>
      <HangingP>
        <strong>E.</strong> The symptoms do not occur exclusively during the
        course of schizophrenia or another psychotic disorder and are not better
        explained by another mental disorder.
      </HangingP>

      <H3>Specify whether:</H3>
      <P>
        <strong>314.01 (F90.2) Combined presentation:</strong> If both Criterion
        A1 (inattention) and Criterion A2 (hyperactivity-impulsivity) are met
        for the past 6 months.
      </P>
      <P>
        <strong>314.00 (F90.0) Predominantly inattentive presentation:</strong>{" "}
        If Criterion A1 is met but Criterion A2 is not met for the past 6
        months.
      </P>
      <P>
        <strong>314.01 (F90.1) Predominantly hyperactive/impulsive presentation:</strong>{" "}
        If Criterion A2 is met and Criterion A1 is not met for the past 6
        months.
      </P>

      <P>
        <em>Specify</em> if: <strong>In partial remission:</strong> When full
        criteria were previously met, fewer than the full criteria have been met
        for the past 6 months, and the symptoms still result in impairment.
      </P>

      <P>
        <em>Specify</em> current severity: <strong>Mild:</strong> Few, if any,
        symptoms beyond those required to make the diagnosis, with no more than
        minor impairment. <strong>Moderate:</strong> Between mild and severe.{" "}
        <strong>Severe:</strong> Many symptoms in excess of those required, or
        particularly severe symptoms, or marked impairment.
      </P>

      <Line />
      <H3>Diagnostic Features</H3>
      <P>
        The essential feature of ADHD is a persistent pattern of inattention
        and/or hyperactivity-impulsivity that interferes with functioning or
        development…
      </P>

      <Indent>
        <P>
          No biological marker is diagnostic for ADHD… (MRI volume differences,
          maturation timing, etc.) are not diagnostic.
        </P>
      </Indent>

      <Line />
      <H3>Prevalence</H3>
      <P>~5% of children; ~2.5% of adults (population surveys).</P>

      <H3>Development and Course</H3>
      <P>
        Often identified in elementary school; hyperactivity tends to decline in
        adolescence, but inattention/impulsivity usually persist into adulthood…
      </P>
      <Indent>
        <P>
          In preschool: mainly hyperactivity. In adolescence: restlessness. In
          adulthood: inattention and impulsivity may remain problematic.
        </P>
      </Indent>

      <H3>Risk and Prognostic Factors</H3>
      <P>
        <strong>Temperamental.</strong> Reduced inhibition/effortful control,
        elevated novelty seeking.
      </P>
      <P>
        <strong>Environmental.</strong> Very low birth weight, prenatal smoking,
        exposure to toxins, etc.
      </P>
      <P>
        <strong>Genetic and physiological.</strong> Substantial heritability;
        elevated in first-degree relatives.
      </P>

      <H3>Culture-Related Diagnostic Issues</H3>
      <P>Rates vary with diagnostic practices; informant/cultural context matters.</P>

      <H3>Gender-Related Diagnostic Issues</H3>
      <P>Males &gt; females overall; females more often present as inattentive.</P>

      <H3>Functional Consequences of ADHD</H3>
      <P>
        Reduced academic/occupational attainment, social rejection, higher risk
        of injury/traffic violations, etc.
      </P>
      <Indent>
        <P>
          Inattention aligns with academic/peer neglect; hyperactivity/impulsivity
          aligns with peer rejection and accidental injury.
        </P>
      </Indent>

      <H3>Differential Diagnosis</H3>
      <P>
        <strong>Oppositional defiant disorder.</strong> Hostility/defiance vs
        inattention.
      </P>
      <P>
        <strong>Intermittent explosive disorder.</strong> Aggression vs impulsivity; rare in childhood.
      </P>
      {/* …continue remaining differentials as needed … */}

      <Subheader>Comorbidity</Subheader>
      <P>
        ODD/common; conduct disorder (subset); anxiety/depression/SLD elevated; SUD/personality disorders in adults may co-occur; tic/OCD/autism can co-occur.
      </P>

      <Line />
      <H3>Other Specified Attention-Deficit/Hyperactivity Disorder</H3>
      <P>
        <strong>314.01 (F90.8)</strong> — used when impairment is present but full criteria aren't met, with stated reason.
      </P>

      <Line />
      <H3>Unspecified Attention-Deficit/Hyperactivity Disorder</H3>
      <P>
        <strong>314.01 (F90.9)</strong> — impairment present, criteria not fully met, reason not specified / insufficient information.
      </P>
    </div>
  );
}
